import { Star } from 'lucide-react';
import type { NavigatorProvider } from './navigator-results.types';
import { avatarColorClass, getInitials } from './avatar';
import { DeliveryModeChips } from './DeliveryModeChips';
import { NavigatorReasonHint } from './NavigatorReasonHint';

type NavigatorAltRowProps = {
  provider: NavigatorProvider;
  /** Feature 087 (ticket 15) — provider is the plan's global Best Match. */
  isBestMatch?: boolean;
  /** Makes this provider the specialty's single selection (feature 080 swap). */
  onSelect?: (provider: NavigatorProvider) => void;
  /** Blocks the Select action while another swap is still persisting. */
  selectDisabled?: boolean;
  selectLabel?: string;
  topMatchLabel?: string;
  whyThisMatchLabel?: string;
};

/**
 * Compact alternate-match row shared by the care-seeker / shared-care-guide
 * views. Mirrors the FP results page's AltMatchRow (small avatar, name ·
 * location, rating, "View" link). After a swap, the AI's rank-1 provider
 * keeps an amber "Top match" tag here so its identity is never lost; the AI
 * reason hides behind a sparkles tooltip so rows stay visually quiet.
 */
export const NavigatorAltRow = ({
  provider,
  isBestMatch = false,
  onSelect,
  selectDisabled = false,
  selectLabel = 'Select',
  topMatchLabel = 'Top match',
  whyThisMatchLabel = 'Why this match',
}: NavigatorAltRowProps) => {
  const displayName = provider.name?.trim() || 'Provider';
  const role = provider.categoryLabel || provider.specialtyName;
  const initials = getInitials(displayName);
  const avatarColor = avatarColorClass(displayName);
  const rating =
    typeof provider.rating === 'number' && provider.rating > 0 ? provider.rating.toFixed(1) : null;
  const aiReason = provider.aiReason?.trim();
  const isDisplacedTopMatch = provider.rankOrder === 1 && !provider.isSelected;

  return (
    <div className="flex items-center gap-4 rounded-xl border border-border-subtle bg-card-base px-4 py-3 transition-shadow duration-150 ease-out motion-reduce:transition-none hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarColor}`}
        aria-hidden
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <span className="truncate">{displayName}</span>
          {isDisplacedTopMatch && (
            <span
              title="Highest-ranked provider for this specialty."
              className="flex-shrink-0 rounded-md bg-[hsl(40_75%_88%)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-[hsl(35_55%_28%)]"
            >
              {topMatchLabel}
            </span>
          )}
          {/* Same gold-foil family as the featured card's Best Match badge
              (feature 087, ticket 15). */}
          {isBestMatch && (
            <span
              title="Highest-scoring provider across this care plan. Primary specialty breaks score ties."
              className="flex-shrink-0 rounded-md border border-[hsl(38_55%_68%)]/60 bg-gradient-to-br from-[hsl(43_75%_94%)] to-[hsl(30_68%_78%)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] text-[hsl(30_65%_26%)]"
            >
              Best Match
            </span>
          )}
        </p>
        <p className="flex flex-wrap items-center text-xs text-text-secondary">
          <span className="truncate">{role}</span>
          {provider.location && (
            <>
              <span className="mx-1.5 text-text-tertiary">·</span>
              {provider.location}
            </>
          )}
          {provider.specialtyDeliveryModes?.length ? (
            <span className="mx-1.5 text-text-tertiary">·</span>
          ) : null}
          <DeliveryModeChips modes={provider.specialtyDeliveryModes} compact />
        </p>
      </div>
      {aiReason && <NavigatorReasonHint reason={aiReason} label={whyThisMatchLabel} />}
      {rating && (
        <span className="flex items-center gap-1 text-xs text-text-secondary">
          <Star className="h-3.5 w-3.5 fill-sage text-sage" aria-hidden />
          <span className="text-text-primary">{rating}</span>
        </span>
      )}
      {onSelect && (
        <button
          type="button"
          onClick={() => onSelect(provider)}
          disabled={selectDisabled}
          className="rounded-full border border-sage px-3 py-1.5 text-xs font-medium text-sage transition-colors duration-150 ease-out hover:bg-sage-bg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
        >
          {selectLabel}
        </button>
      )}
      <a
        href={`/providers/${provider.id}?discoverySource=NAVIGATOR`}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-full border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-primary transition-colors duration-150 ease-out hover:border-sage hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
      >
        View
      </a>
    </div>
  );
};
